import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, ToggleButton, ToggleButtonGroup } from '@mui/material'
import classNames from 'classnames'
import colorNames from 'colornames'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import styles from './ProductFilterAttribute.module.scss'
import { FiltersAttributeInterface } from '../../../interfaces/FiltersInterface'
import { DEFAULT_LANGUAGE_ISO_CODE } from '../../../utils/locale'
import { PRODUCT_ATTRIBUTE_IDS } from '../../../utils/product'
import { _at } from '../../../utils/translations'
import ProductFilterActiveCount from '../ProductFilterActiveCount/ProductFilterActiveCount'

export enum FILTER_BUTTON_SIZE {
  NORMAL,
  LARGE,
}

interface Props {
  selectedValues: number[]
  attribute: FiltersAttributeInterface
  buttonSize: FILTER_BUTTON_SIZE
  onSelectionChange: (attributeId: number, valueId: number, checked: boolean) => void
}

const ProductFilterAttribute: React.FC<Props> = (props: Props) => {
  const [selectedValues, setSelectedValues] = useState<number[]>(props.selectedValues)

  useEffect(() => {
    setSelectedValues(props.selectedValues)
  }, [JSON.stringify(props.selectedValues)])

  const handleButtonClick = (valueId: number) => {
    const checked = !selectedValues.includes(valueId)
    let newSelectedValues = [...selectedValues]
    if (checked) {
      newSelectedValues.push(valueId)
    } else {
      newSelectedValues = selectedValues.filter(value => value !== valueId)
    }
    setSelectedValues(newSelectedValues)
    props.onSelectionChange(props.attribute.id, valueId, checked)
  }

  const options = props.attribute.values.map(value => {
    const optionLabel = value.translatable ? _at('value', value.translations, DEFAULT_LANGUAGE_ISO_CODE) : value.stringValue
    return (
      <ToggleButton
        key={`product-filter-attribute-${props.attribute.id}-value-${value.id}`}
        value={value.id}
        classes={{
          root: classNames(styles['toggle-button-group__button'], {
            [styles['toggle-button-group__button--normal']]: props.buttonSize === FILTER_BUTTON_SIZE.NORMAL,
            [styles['toggle-button-group__button--large']]: props.buttonSize === FILTER_BUTTON_SIZE.LARGE,
          }),
          selected: styles['toggle-button-group__button--selected'],
        }}
        onChange={() => {
          handleButtonClick(value.id)
        }}
      >
        {props.attribute.id === PRODUCT_ATTRIBUTE_IDS.COLOR ? (
          <div
            className={styles['toggle-button-group__button__color-circle']}
            style={{
              backgroundColor: colorNames(optionLabel) || optionLabel,
            }}
          />
        ) : null}
        <div className={styles['toggle-button-group__button__label']}>{optionLabel}</div>
      </ToggleButton>
    )
  })

  return (
    <Accordion elevation={0} classes={{ root: styles['product-filter-attribute__accordion'] }}>
      <AccordionSummary
        classes={{
          root: styles['accordion_summary__root'],
          content: styles['accordion_summary__content'],
          expanded: styles['accordion_summary__content--expanded'],
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        {_at('name', props.attribute.translations)}
        {selectedValues.length ? <ProductFilterActiveCount count={selectedValues.length} /> : null}
      </AccordionSummary>
      <AccordionDetails>
        <ToggleButtonGroup value={selectedValues} classes={{ root: styles['product-filter-attribute__toggle-button-group'] }}>
          {options}
        </ToggleButtonGroup>
      </AccordionDetails>
    </Accordion>
  )
}

export default ProductFilterAttribute
